import { useMemo, useRef, useState } from "react";
import { PVK_ANALYTE_ORDER, type LabAnalyteCode, type LabResult, type ReadLabResultAnalyte } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import {
  LabTrendCombinedChart,
  PvkAccordion,
  ResultRow,
  ResultSlip,
  useAnalyteCopy,
  useRangeCopy,
  type PvkAnalyteRow,
} from "@ferrivia/lab-ui";
import { Text } from "@ferrivia/ui";
import { AnalyteInfoSheet, type AnalyteInfo } from "../components/AnalyteInfoSheet";
import { ResultTrend } from "../components/ResultTrend";
import { buildTrend, formatAxisDate, formatCollectedOn } from "../screens/ResultsScreen/trend";
import s from "./LabsPart.module.css";

const PVK_CODES: ReadonlySet<string> = new Set(PVK_ANALYTE_ORDER);

const CLIENT_RANGE_ATTRIBUTION = { attributeLab: false } as const;

export interface LabsPartProps {
  labResults: LabResult[];
  container: HTMLElement | null;
}

export function LabsPart({ labResults, container }: LabsPartProps) {
  const { t } = useTranslation("client");
  const { tag } = useFormatters();
  const tracker = useTracker();
  const { labelFor } = useAnalyteCopy();
  const { lineFor } = useRangeCopy(CLIENT_RANGE_ATTRIBUTION);
  const drawSelectionTracked = useRef(false);
  const [openAnalyte, setOpenAnalyte] = useState<AnalyteInfo | null>(null);

  const latest = labResults[0] ?? null;
  const previous = labResults[1] ?? null;
  const trend = useMemo(() => buildTrend(labResults, labelFor), [labResults, labelFor]);

  if (!latest) {
    return null;
  }

  function asideFor(analyte: ReadLabResultAnalyte) {
    const before = previous?.analytes.find((a) => a.analyteCode === analyte.analyteCode);
    if (!before || before.unit !== analyte.unit) {
      return undefined;
    }
    return <ResultTrend current={analyte.value} previous={before.value} unit={analyte.unit} />;
  }

  function handleDrawSelected() {
    if (drawSelectionTracked.current) {
      return;
    }
    drawSelectionTracked.current = true;
    tracker.track("summary.trend_draw_selected", { draw_count: trend?.dates.length ?? 0 });
  }

  // Never the value: the explainer must not vary with what this client measured (REQ-MDR-002).
  function handleAnalyteOpened(analyte: ReadLabResultAnalyte, labCode: string | null) {
    tracker.track("summary.analyte_opened", { analyte_code: analyte.analyteCode });
    setOpenAnalyte({
      analyteCode: analyte.analyteCode,
      unit: analyte.unit,
      refText: analyte.refText,
      labCode,
    });
  }

  const mainAnalytes = latest.analytes.filter((a) => !PVK_CODES.has(a.analyteCode));
  const pvkRows: PvkAnalyteRow[] = PVK_ANALYTE_ORDER.flatMap((code: LabAnalyteCode) => {
    const analyte = latest.analytes.find((a) => a.analyteCode === code);
    if (!analyte) {
      return [];
    }
    return [
      {
        analyteCode: analyte.analyteCode,
        label: labelFor(analyte.analyteCode),
        value: analyte.value,
        unit: analyte.unit,
        rangeText: lineFor(analyte.refText, latest.labCode, analyte.unit),
        aside: asideFor(analyte),
      },
    ];
  });

  return (
    <div className={s.root}>
      <ResultSlip
        when={t("results.collectedOn", "{{date}}", { date: formatCollectedOn(latest.collectedOn, tag) })}
        provenance={
          latest.labCode === null ? (
            <span className={s.selfMark}>{t("results.selfReported", "Itse ilmoitettu")}</span>
          ) : undefined
        }
      >
        {mainAnalytes.map((analyte) => (
          <ResultRow
            key={analyte.analyteCode}
            label={labelFor(analyte.analyteCode)}
            value={analyte.value}
            unit={analyte.unit}
            rangeText={lineFor(analyte.refText, latest.labCode, analyte.unit)}
            aside={asideFor(analyte)}
            onClick={() => handleAnalyteOpened(analyte, latest.labCode)}
          />
        ))}
        <PvkAccordion
          rows={pvkRows}
          onRowOpened={(code) => {
            const analyte = latest.analytes.find((a) => a.analyteCode === code);
            if (analyte) {
              handleAnalyteOpened(analyte, latest.labCode);
            }
          }}
          onToggled={(expanded) => tracker.track("summary.disclosure_toggled", { section_id: "pvk_accordion", expanded })}
        />
      </ResultSlip>

      {trend && (
        <div className={s.trend}>
          <LabTrendCombinedChart
            series={trend.series}
            axis={trend.axis}
            dates={trend.dates}
            formatAxisDate={(collectedOn) => formatAxisDate(collectedOn, tag)}
            formatDrawDate={(collectedOn) => formatCollectedOn(collectedOn, tag)}
            onDrawSelected={handleDrawSelected}
          />
          <Text as="p" size="xs" className={s.hint}>
            {t("summary.labs.trendHint", "Napauta päivämäärää nähdäksesi sen arvot.")}
          </Text>
        </div>
      )}

      <AnalyteInfoSheet
        analyte={openAnalyte}
        onClose={() => setOpenAnalyte(null)}
        container={container}
        dismissKey="summaryAnalyteInfo"
      />
    </div>
  );
}
